// Общие типы данных и DTO между backend / agent / pwa.

/**
 * Максимальная длина пользовательского промпта (в символах).
 * Применяется к POST /sessions/project/:id, /sessions/:id/prompts, /sessions/:id/resume.
 * Backend возвращает 400 `prompt_too_long` при превышении, PWA блокирует Send заранее.
 */
export const MAX_PROMPT_LENGTH = 50_000;

export type MachineOS = 'darwin' | 'linux' | 'win32';

export type SessionStatus =
  | 'idle'
  | 'running'
  | 'waiting_permission'
  | 'finished'
  | 'error';

export type PermissionStatus =
  | 'pending'
  | 'approved'
  | 'denied'
  | 'timeout';

export type PermissionMode =
  | 'default'
  | 'acceptEdits'
  | 'auto'
  | 'bypassPermissions'
  | 'plan';

/**
 * Уровень «усилий» модели — мапится в `maxThinkingTokens` для SDK.
 * Чем выше — тем больше токенов модель тратит на extended thinking,
 * тем дороже ход. Нужен для UI выбора при создании сессии.
 */
export type EffortLevel = 'low' | 'medium' | 'high' | 'extra_high' | 'max';

export type SessionEventType =
  | 'user_prompt'
  | 'assistant_text'
  | 'tool_use'
  | 'tool_result'
  | 'stdout'
  | 'stderr'
  | 'system';

export interface Machine {
  id: string;
  name: string;
  os: MachineOS | null;
  hostname: string | null;
  online: boolean;
  lastSeenAt: number | null;
  createdAt: number;
}

export interface Project {
  id: string;
  machineId: string;
  name: string;
  path: string;
  createdAt: number;
  // Метка последней активности проекта — max по last_event_at сессий.
  // null для проектов без сессий/событий. Используется для сортировки
  // списка проектов в PWA.
  lastEventAt: number | null;
  // Если не null — проект в архиве (показывается в свернутом аккордеоне).
  archivedAt: number | null;
}

export interface Session {
  id: string;
  projectId: string;
  claudeSessionId: string | null;
  title: string | null;
  status: SessionStatus;
  startedAt: number;
  endedAt: number | null;
  costUsd: number;
}

export interface SessionEvent {
  id: string;
  sessionId: string;
  seq: number;
  type: SessionEventType;
  content: string; // JSON string, shape depends on type
  createdAt: number;
}

export interface PermissionRequest {
  id: string;
  sessionId: string;
  toolName: string;
  toolInput: string; // JSON string
  status: PermissionStatus;
  decidedByDeviceId: string | null;
  decisionReason: string | null;
  createdAt: number;
  decidedAt: number | null;
  timeoutAt: number | null;
  // Контекст для inbox: чтобы видеть, к какому проекту/сессии относится
  // запрос — без отдельного fetch'а на стороне PWA.
  projectName: string;
  sessionTitle: string | null;
}

export interface Device {
  id: string;
  name: string;
  userAgent: string | null;
  hasPush: boolean;
  createdAt: number;
  lastSeenAt: number | null;
}

// REST request/response DTOs
export interface RequestCodeDto {
  email: string;
}

export interface VerifyCodeDto {
  email: string;
  code: string;
  deviceName: string;
  userAgent?: string;
}

export interface VerifyCodeResponse {
  jwt: string;
  deviceId: string;
  userId: string;
}

export interface CreateMachineDto {
  name: string;
}

export interface CreateMachineResponse {
  machineId: string;
  token: string;
}

export interface CreateSessionDto {
  prompt: string;
  /**
   * Идентификаторы заранее загруженных attachment'ов (см. `POST /api/v1/attachments`).
   * Принадлежность пользователю/сессии backend проверяет на сервере.
   */
  attachments?: Array<{ id: string }>;
  model?: string;
  permissionMode?: PermissionMode;
  effort?: EffortLevel;
}

export interface SendPromptDto {
  prompt: string;
  attachments?: Array<{ id: string }>;
}

export interface ResumeSessionDto {
  prompt: string;
  attachments?: Array<{ id: string }>;
  model?: string;
  permissionMode?: PermissionMode;
  effort?: EffortLevel;
}

export interface PermissionDecisionDto {
  reason?: string;
}

// === Attachments (изображения, прикрепляемые к промптам) ===

/**
 * Whitelist MIME-типов для attachment'ов. SVG не пускаем (XSS),
 * HEIC/HEIF — отдельная история (нужна серверная конвертация).
 */
export type AttachmentMime = 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif';

/**
 * Полный референс на загруженный attachment, который backend возвращает PWA
 * после успешного `POST /api/v1/attachments`.
 */
export interface AttachmentRef {
  id: string;
  mimeType: AttachmentMime;
  byteSize: number;
  width?: number;
  height?: number;
  sha256: string;
}

/**
 * Источник attachment'а: пришёл ли он от пользователя (PWA upload),
 * был ли извлечён из tool_result MCP-инструмента (например Figma/Playwright)
 * или сгенерирован Claude как assistant image-блок.
 */
export type AttachmentSource = 'user_upload' | 'tool_result' | 'assistant_image';

/**
 * Блоки content'а внутри `events.content` (когда content — массив, а не голая строка).
 * Используется для `user_prompt`, в перспективе — для `assistant_text` и `tool_result`,
 * где SDK может прислать массив с image-блоками.
 *
 * `image` хранит только ссылку на attachment; бинарь живёт в `data/attachments/...`.
 */
export type ContentBlock =
  | { type: 'text'; text: string }
  | { type: 'image'; attachmentId: string; mediaType: AttachmentMime; sha256?: string };

// === Plan (файл плана проекта для quick actions) ===

/**
 * Пункт плана, распарсенный из markdown-файла.
 * Поддерживаются чекбоксы `- [ ]` / `- [x]` и заголовки `## `.
 */
export interface PlanItem {
  /** Уникальный id в рамках файла (line number или hash). */
  id: string;
  /** Текст пункта (без маркера чекбокса). */
  text: string;
  /** Уровень вложенности (0 = top-level). */
  level: number;
  /** Завершён ли пункт (чекбокс [x] или пометка ✅). */
  done: boolean;
  /** Номер строки в файле (для возможной навигации). */
  line: number;
}

export interface PlanFile {
  /** Путь к файлу плана относительно корня проекта. */
  path: string;
  /** Распарсенные пункты. */
  items: PlanItem[];
}

export interface GetPlanResponse {
  plan: PlanFile | null;
  /** Причина отсутствия плана, если plan === null. */
  error?: string | undefined;
}
